
import { useState, useEffect } from 'react';
import { playerStatsService, PlayerStat, PlayerTotalStats } from '@/services/playerStatsService';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { toast } from 'sonner';

export const usePlayerStats = (playerId?: string) => {
  const { user } = useUnifiedUser();
  const [stats, setStats] = useState<PlayerStat[]>([]);
  const [totalStats, setTotalStats] = useState<PlayerTotalStats[]>([]);
  const [loading, setLoading] = useState(false);

  const targetPlayerId = playerId || user?.id;

  const fetchPlayerStats = async () => {
    if (!targetPlayerId) return;
    
    setLoading(true);
    try {
      const playerStats = await playerStatsService.getPlayerStats(targetPlayerId);
      const playerTotalStats = await playerStatsService.getPlayerTotalStats(targetPlayerId);
      
      setStats(playerStats);
      setTotalStats(playerTotalStats);
    } catch (error) {
      console.error('Error fetching player stats:', error);
      toast.error('Failed to load player stats');
    } finally {
      setLoading(false);
    }
  };

  const submitStat = async (
    gameId: number,
    sportId: number,
    statType: string,
    statValue: number
  ) => {
    if (!targetPlayerId) {
      toast.error('Player not identified');
      return false;
    }

    const result = await playerStatsService.submitPlayerStats(
      targetPlayerId,
      gameId,
      sportId,
      statType,
      statValue,
      targetPlayerId, // submitterId - using player's own ID for self-submission
      'player' // submitterRole
    );

    if (result.success) {
      toast.success('Stats submitted successfully, awaiting confirmation');
      fetchPlayerStats(); // Refresh stats
      return true;
    } else {
      toast.error(result.error || 'Failed to submit stats');
      return false;
    }
  };

  useEffect(() => {
    fetchPlayerStats();
  }, [targetPlayerId]);

  return {
    stats,
    totalStats,
    loading,
    submitStat,
    refreshStats: fetchPlayerStats
  };
};

export const useStatsConfirmation = (gameId?: number) => {
  const [unconfirmedStats, setUnconfirmedStats] = useState<PlayerStat[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useUnifiedUser();

  const fetchUnconfirmedStats = async () => {
    if (!gameId) return;
    
    setLoading(true);
    try {
      const stats = await playerStatsService.getUnconfirmedStats(gameId);
      setUnconfirmedStats(stats);
    } catch (error) {
      console.error('Error fetching unconfirmed stats:', error);
      toast.error('Failed to load stats for confirmation');
    } finally {
      setLoading(false);
    }
  };

  const confirmStat = async (statId: number) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return false;
    }

    const result = await playerStatsService.confirmStat(statId, user.id);

    if (result.success) {
      toast.success('Stat confirmed successfully');
      fetchUnconfirmedStats(); // Refresh unconfirmed stats
      return true;
    } else {
      toast.error(result.error || 'Failed to confirm stat');
      return false;
    }
  };

  useEffect(() => {
    fetchUnconfirmedStats();
  }, [gameId]);

  return {
    unconfirmedStats,
    loading,
    confirmStat,
    refreshStats: fetchUnconfirmedStats
  };
};
